/**
 * The globe component.
 *
 * INVARIANT 7 — the style carries no glyphs, no sprite and no symbol layer, so
 * a label cannot render even by accident. See `style.ts`.
 *
 * DESIGN NOTES
 *
 * The map is created exactly once, on mount, and is never re-created by a prop
 * change. Props are *inputs to effects* that drive the same internal functions
 * the imperative handle drives; the handle is the primary interface and the
 * props are conveniences. That is what makes a rung transition (highlight, pin,
 * fly) cost nothing but a feature-state write.
 *
 * Highlights are feature-state only. No `setPaintProperty`, no layer rebuild,
 * no source reload — the fill colour is a `case` expression over
 * `["feature-state", "hl"]` that the GPU re-evaluates for free.
 *
 * Pins are GPU circle layers on a small GeoJSON source rather than DOM
 * markers. DOM markers cost a reflow per frame while the globe spins and are
 * the classic reason a MapLibre globe drops to 40fps on a phone.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Map as MapLibreMap } from 'maplibre-gl';
import type { GeoJSONSource, MapGeoJSONFeature } from 'maplibre-gl';
import type { Geometry as GeoJSONGeometry, Position } from 'geojson';

import { isFrontFacing } from './geo';
import { frameForCountry, orientFramingFor } from './framing';
import { magnifyPlan } from './magnify';
import type { MarkCollection, MarkRecord } from './marks';
import { markAnchor, markCollection, pulseOpacity, pulsePhase, pulseRadiusPx } from './marks';
import { publishCameraProbe, retractCameraProbe } from './probe';
import { globePxPerDeg, homeZoomFor } from './projection';
import { ensureRuntimeStyles } from './runtimeStyles';
import type { PointCandidate, SnapProbes, SnapResolution } from './snap';
import { snapResolve } from './snap';
import { isSubResolution, pickTarget, pickTargets } from './targets';
import type { PinCollection, PinKind, PinProps, RegionCollection } from './style';
import {
  HL_KEY,
  LAYER,
  MARK_PULSE_OPACITY_PROP,
  MARK_PULSE_RADIUS_PROP,
  POLYGON_PICK_LAYERS,
  REGION_PICK_LAYERS,
  SOURCE,
  buildStyle,
  emptyRegionCollection,
  microstateDots,
} from './style';
import {
  CAMERA,
  HOME_FILL_FRACTION,
  HOME_VIEW,
  IDLE_SPIN_DEG_PER_SEC,
  MOBILE_HOME_FILL_FRACTION,
  SNAP,
  TAP_MAX_MS,
  TAP_SLOP_PX,
} from './theme';
import type { GlobeHandle, GlobePick, GlobeProps } from './types';
import { BORDER_LINE_LAYERS, applyImageryPatch } from './imagery';
import { skinById, tuningFor } from './skins';
import type { SkinId } from './skins';
import type { WaveImage } from './waves';
import { WAVE_IMAGE_ID, WAVE_PIXEL_RATIO, createWaveImage } from './waves';

/**
 * The home zoom for the viewport we are actually in.
 *
 * `HOME_VIEW.zoom` is one fixed number, so the globe came out the same size on
 * every screen: a small disc adrift on a desktop, and wider than a phone. This
 * fits it to the short edge instead, which is also the fix for "after a reveal
 * it zooms way too far out" — the reveal flies in close, and returning to a
 * fixed 0.9 read as a jarring pull-back rather than a return to where you were.
 */
function isMobilePortrait(container: HTMLElement | null): boolean {
  const width = container?.clientWidth ?? 0;
  const height = container?.clientHeight ?? 0;
  return width > 0 && width < 768 && height > width;
}

function homeZoom(container: HTMLElement | null): number {
  const width = container?.clientWidth ?? 0;
  const height = container?.clientHeight ?? 0;
  const shortEdge = Math.min(width, height);
  if (shortEdge <= 0) return HOME_VIEW.zoom;

  /*
   * Keep MapLibre's original centred camera position. On portrait phones the
   * globe should be approximately viewport-height, so it fills the playfield
   * instead of sitting there as a small disc that needs several pinch-zooms
   * before the player can start searching. Wider layouts retain the inset
   * short-edge framing used on desktop.
   */
  const framingEdge = isMobilePortrait(container) ? height : shortEdge;
  const fill = isMobilePortrait(container) ? MOBILE_HOME_FILL_FRACTION : HOME_FILL_FRACTION;
  return homeZoomFor(framingEdge, fill, CAMERA.minZoom, CAMERA.maxZoom);
}

/**
 * The skin the game boots to. Imagery, not the flat vector look: measured
 * legibility is better (21 targets clear / 0 lost, against the vector globe's
 * 13 clear / 5 faint / 1 lost) because the raster skins carry the border casing
 * and dot-halo kit the vector one never had.
 *
 * `terra` rather than `marble`: same Blue Marble colour, with shaded relief
 * multiplied in so mountains read as mountains, and with the source bathymetry
 * kept instead of flattened to one slate colour, so the ocean has depth in it.
 * Its sea is baked translucent, which is what the animated swell shows through.
 */
const DEFAULT_SKIN: SkinId = 'terra';

/**
 * How dark an eclipse goes, and how long it takes to get there.
 *
 * 0.78 rather than 1: the coastlines have to survive, because they are what
 * makes an eclipsed round answerable at all. Taking the borders is a handicap
 * on knowledge; taking the coastlines would be a blindfold.
 *
 * 900ms is long enough to read as weather rather than a dropped frame, and
 * short enough to finish inside the arm window before the round opens.
 */
const ECLIPSE_OPACITY = 0.78;
const ECLIPSE_MS = 900;

/**
 * The two paint values an eclipse frame paints, at progress `t` in [0, 1].
 *
 * PURE, AND SEPARATE FROM THE ANIMATION, because the arithmetic is the part
 * that can be wrong in a way nobody notices: an eased pair that does not start
 * where the world currently is, or does not finish where it is going, produces
 * a jump at one end that reads as a glitch — which is the single thing this
 * mechanic cannot afford to look like. A WebGL context is not needed to check
 * that, and is not available to a test.
 *
 * `shadow` is the eclipse layer's fill opacity; `ink` is the border layers'
 * line opacity. They move together off one clock so the world darkens as the
 * borders leave, rather than in two separate visible steps.
 */
export function eclipseFrame(t: number, on: boolean, wasOn: boolean): {
  shadow: number;
  ink: number;
} {
  const p = Math.min(1, Math.max(0, t));
  const e = p < 0.5 ? 2 * p * p : 1 - (-2 * p + 2) ** 2 / 2;
  const fromShadow = wasOn ? ECLIPSE_OPACITY : 0;
  const toShadow = on ? ECLIPSE_OPACITY : 0;
  const fromInk = wasOn ? 0 : 1;
  const toInk = on ? 0 : 1;
  return {
    shadow: fromShadow + (toShadow - fromShadow) * e,
    ink: fromInk + (toInk - fromInk) * e,
  };
}

/**
 * Zoom at which `swellLayer()`'s opacity ramp has reached zero. Past it the
 * pattern is invisible, so the repaint loop that animates it stops paying for
 * frames nobody can see.
 */
const SWELL_FADED_ZOOM = 4.5;

type HighlightKind = 'correct' | 'incorrect' | 'neutral';

interface PinRecord {
  readonly id: string;
  readonly lngLat: readonly [number, number];
  readonly kind: PinKind;
}

/** Where a subdivision's mark lands and what the camera has to cover to hold it. */
interface RegionEntry {
  readonly anchor: [number, number];
  readonly bounds: [number, number, number, number];
}

interface Runtime {
  map: MapLibreMap | null;
  loaded: boolean;
  /** The eclipse shadow is currently drawn. */
  eclipseOn: boolean;
  /** In-flight eclipse animation, so a second call cannot fight the first. */
  eclipseRaf: number | null;
  disposed: boolean;
  interactive: boolean;
  idleSpin: boolean;
  /** Set by any pointer-down or camera command; blocks spin until reset. */
  spinSuppressed: boolean;
  spinRaf: number | null;
  spinLastTs: number;
  reducedMotion: boolean;
  snapRadiusPx: number;
  highlights: Map<string, HighlightKind>;
  pins: Map<string, PinRecord>;
  marks: Map<string, MarkRecord>;
  /**
   * Subdivision anchors and bounds by ISO 3166-2, or null when no collection
   * has been handed in.
   *
   * Non-null is what puts the globe in REGION MODE: taps resolve against the
   * subdivision fill instead of the country fill, highlights and marks are
   * written to the subdivision source, and `flyToCountry` frames a subdivision.
   * One flag, derived from the data actually present, rather than a second
   * prop that could disagree with it.
   */
  regions: Map<string, RegionEntry> | null;
  /**
   * The collection itself, held so `onLoad` can push it into the source.
   *
   * MapLibre drops `setData` on a style that has not finished loading, and the
   * client hands the polygons over as soon as its fetch resolves — which is
   * usually BEFORE the map fires `load`. Without this the first (and only)
   * delivery was silently discarded and every tap fell through an empty
   * subdivision layer to `null`, which the game grades as open ocean.
   */
  regionData: RegionCollection | null;
  /** `performance.now()` the current pulse started at, or null when idle. */
  pulseStartedAt: number | null;
  pulseRaf: number | null;
  /** The animated ocean pattern, or null for a skin with no swell. */
  wave: WaveImage | null;
  swellRaf: number | null;
  onPick: GlobeProps['onPick'];
  onReady: GlobeProps['onReady'];
  onDisambiguate: GlobeProps['onDisambiguate'];
  disambiguate: boolean;
}

/** Codes that live in the point source rather than the polygon source. */
let microCodes: ReadonlySet<string> | null = null;
function microCodeSet(): ReadonlySet<string> {
  microCodes ??= new Set(microstateDots().map((d) => d.cca3));
  return microCodes;
}

/**
 * Every source a highlight for this country has to be written to.
 *
 * A microstate is drawn TWICE — as its stand-in dot and, since it now has one,
 * as its real outline — and the two live in different sources. A highlight
 * written to only one of them lights up the dot and leaves the island grey, or
 * the other way round once the dot has retired at close zoom. Feature-state is
 * per source, so there is nothing cleverer to do than write both.
 */
function sourcesFor(rt: Runtime, code: string): readonly string[] {
  // In region mode the only polygons on screen that mean anything are the
  // subdivisions, and a country highlight underneath fifty of them is invisible.
  if (rt.regions) return [SOURCE.regions];
  return microCodeSet().has(code) ? [SOURCE.micro, SOURCE.microShape] : [SOURCE.countries];
}

function wrapLng(lng: number): number {
  return (((lng + 180) % 360) + 360) % 360 - 180;
}

/**
 * The entity a queried feature stands for. Properties arrive as `any` from
 * MapLibre, so they are narrowed once, here.
 *
 * `code` is read FIRST and without a length check: a subdivision is `US-OH`,
 * and the three-character test below is what a country code is, not what an
 * identifier is. The two sources are never queried together — see
 * `REGION_PICK_LAYERS` — so there is no ambiguity to resolve, only two shapes
 * to read.
 */
function codeOf(feature: MapGeoJSONFeature): string | null {
  const region: unknown = feature.properties['code'];
  if (typeof region === 'string' && region.length > 0) return region;
  if (typeof feature.id === 'string' && feature.id.length === 3) return feature.id;
  const raw: unknown = feature.properties['cca3'];
  return typeof raw === 'string' && raw.length === 3 ? raw : null;
}

/** Largest-ring, area-weighted centroid, and total bounds, of one region. */
function regionEntry(geometry: GeoJSONGeometry): RegionEntry | null {
  const rings: Position[][] = [];
  if (geometry.type === 'Polygon') {
    const ring = geometry.coordinates[0];
    if (ring) rings.push(ring);
  } else if (geometry.type === 'MultiPolygon') {
    for (const poly of geometry.coordinates) {
      const ring = poly[0];
      if (ring) rings.push(ring);
    }
  }
  if (rings.length === 0) return null;

  let minLng = 180;
  let minLat = 90;
  let maxLng = -180;
  let maxLat = -90;
  let best: Position[] | null = null;
  let bestSpan = -1;
  let bestBounds: [number, number, number, number] | null = null;
  for (const ring of rings) {
    let rMinLng = 180;
    let rMinLat = 90;
    let rMaxLng = -180;
    let rMaxLat = -90;
    for (const point of ring) {
      const lng = point[0] as number;
      const lat = point[1] as number;
      if (lng < rMinLng) rMinLng = lng;
      if (lat < rMinLat) rMinLat = lat;
      if (lng > rMaxLng) rMaxLng = lng;
      if (lat > rMaxLat) rMaxLat = lat;
    }
    // Span rather than a shoelace area: this only has to pick Michigan's Lower
    // Peninsula over its Upper one and Alaska's mainland over the Aleutians,
    // and a bounding span does that without a second area routine.
    const span = (rMaxLng - rMinLng) * (rMaxLat - rMinLat);
    if (span > bestSpan) {
      bestSpan = span;
      best = ring;
      bestBounds = [rMinLng, rMinLat, rMaxLng, rMaxLat];
    }
    if (rMinLng < minLng) minLng = rMinLng;
    if (rMinLat < minLat) minLat = rMinLat;
    if (rMaxLng > maxLng) maxLng = rMaxLng;
    if (rMaxLat > maxLat) maxLat = rMaxLat;
  }
  if (!best) return null;

  let twiceArea = 0;
  let x = 0;
  let y = 0;
  for (let i = 0; i < best.length - 1; i += 1) {
    const x0 = best[i]?.[0] as number;
    const y0 = best[i]?.[1] as number;
    const x1 = best[i + 1]?.[0] as number;
    const y1 = best[i + 1]?.[1] as number;
    const cross = x0 * y1 - x1 * y0;
    twiceArea += cross;
    x += (x0 + x1) * cross;
    y += (y0 + y1) * cross;
  }
  const anchor: [number, number] =
    twiceArea === 0
      ? [(minLng + maxLng) / 2, (minLat + maxLat) / 2]
      : [x / (3 * twiceArea), y / (3 * twiceArea)];

  /*
   * ALASKA. Its Aleutian chain crosses the antimeridian, so the union of its
   * rings runs from -179.1 to +179.8 and framing that box zooms out to the
   * whole planet — the reveal for Alaska showed the player everything except
   * Alaska. A span over half the world is never a real extent, it is a
   * wraparound, and the largest ring is the part worth looking at.
   */
  const wrapped = maxLng - minLng > 180;
  const bounds: [number, number, number, number] =
    wrapped && bestBounds ? bestBounds : [minLng, minLat, maxLng, maxLat];
  return { anchor, bounds };
}

/** Index a subdivision collection for marking and framing. */
function indexRegions(collection: RegionCollection): Map<string, RegionEntry> {
  const index = new Map<string, RegionEntry>();
  for (const f of collection.features) {
    const code = f.properties.code;
    const entry = regionEntry(f.geometry);
    if (code && entry) index.set(code, entry);
  }
  return index;
}

function pinCollection(pins: ReadonlyMap<string, PinRecord>): PinCollection {
  return {
    type: 'FeatureCollection',
    features: [...pins.values()].map((p) => ({
      type: 'Feature' as const,
      id: p.id,
      properties: { pid: p.id, kind: p.kind } satisfies PinProps,
      geometry: { type: 'Point' as const, coordinates: [p.lngLat[0], p.lngLat[1]] },
    })),
  };
}

interface ToggleHandler {
  enable(): void;
  disable(): void;
}

function applyInteractive(map: MapLibreMap, on: boolean): void {
  const toggle = (h: ToggleHandler): void => {
    if (on) h.enable();
    else h.disable();
  };
  toggle(map.scrollZoom);
  toggle(map.dragPan);
  toggle(map.keyboard);
  toggle(map.doubleClickZoom);
  toggle(map.touchZoomRotate);
  toggle(map.boxZoom);
}

function stopSpin(rt: Runtime): void {
  if (rt.spinRaf !== null) {
    cancelAnimationFrame(rt.spinRaf);
    rt.spinRaf = null;
  }
  rt.spinLastTs = 0;
}

function maybeStartSpin(rt: Runtime): void {
  const map = rt.map;
  if (!map || !rt.loaded || rt.disposed) return;
  if (!rt.idleSpin || rt.spinSuppressed || rt.reducedMotion) return;
  if (rt.spinRaf !== null) return;

  const step = (ts: number): void => {
    const m = rt.map;
    if (rt.disposed || !m || !rt.idleSpin || rt.spinSuppressed) {
      rt.spinRaf = null;
      return;
    }
    if (rt.spinLastTs === 0) rt.spinLastTs = ts;
    // Clamp dt so a backgrounded tab does not resume with a 3-second jump.
    const dt = Math.min(120, ts - rt.spinLastTs);
    rt.spinLastTs = ts;
    const c = m.getCenter();
    m.jumpTo({ center: [wrapLng(c.lng + (IDLE_SPIN_DEG_PER_SEC * dt) / 1000), c.lat] });
    rt.spinRaf = requestAnimationFrame(step);
  };

  rt.spinLastTs = 0;
  rt.spinRaf = requestAnimationFrame(step);
}

function stopSwell(rt: Runtime): void {
  if (rt.swellRaf !== null) {
    cancelAnimationFrame(rt.swellRaf);
    rt.swellRaf = null;
  }
}

/**
 * Drive the ocean swell.
 *
 * Two things have to happen and MapLibre does neither on its own: the pattern's
 * pixels have to be rewritten (`advance()`), and the new version has to be
 * pushed so `getPattern()` re-copies it into the atlas (`updateImage`). See the
 * header of `waves.ts` for why the documented `StyleImageInterface.render()`
 * hook is not the mechanism here.
 *
 * The loop is a `requestAnimationFrame` chain but it does NOT repaint every
 * frame: `advance()` throttles itself, and a repaint is requested only on the
 * frames it accepts. So the globe renders at the swell's ~15fps rather than at
 * 60, and the rest of the callbacks cost a zoom read and a compare.
 *
 * It also runs only while there is something to see: never under
 * `prefers-reduced-motion`, and never while the camera is past the zoom where
 * `swellLayer()` has already faded the pattern to nothing. The idle spin
 * repaints on its own, but it is not always on and the swell cannot depend on
 * it.
 */
function maybeStartSwell(rt: Runtime): void {
  const map = rt.map;
  if (!map || !rt.loaded || rt.disposed || !rt.wave) return;
  if (rt.reducedMotion) return;
  if (rt.swellRaf !== null) return;

  const step = (): void => {
    const m = rt.map;
    const wave = rt.wave;
    if (rt.disposed || !m || !wave || rt.reducedMotion) {
      rt.swellRaf = null;
      return;
    }
    if (m.getZoom() < SWELL_FADED_ZOOM && wave.advance()) {
      // `hasImage` guards the window between a style swap dropping the image
      // and the `styledata` handler putting it back.
      if (m.hasImage(WAVE_IMAGE_ID)) {
        m.updateImage(WAVE_IMAGE_ID, wave);
        m.triggerRepaint();
      }
    }
    rt.swellRaf = requestAnimationFrame(step);
  };
  rt.swellRaf = requestAnimationFrame(step);
}

/**
 * Every camera command stops the spin and keeps it stopped. "Does not resume
 * mid-round" is the requirement; only `resetView()` or toggling the `idleSpin`
 * prop off and on again brings it back.
 */
function cameraTo(
  rt: Runtime,
  target: { center: [number, number]; zoom?: number; bearing?: number; pitch?: number },
  durationMs: number,
  fly: boolean,
): number {
  const map = rt.map;
  if (!map) return 0;
  stopSpin(rt);
  rt.spinSuppressed = true;

  const duration = rt.reducedMotion ? 0 : Math.max(0, durationMs);
  if (duration === 0) {
    map.jumpTo(target);
  } else if (fly) {
    map.flyTo({ ...target, duration, curve: 1.25, speed: 1.1 });
  } else {
    map.easeTo({ ...target, duration });
  }
  return duration;
}

function flushHighlights(rt: Runtime): void {
  const map = rt.map;
  if (!map || !rt.loaded) return;
  for (const [code, kind] of rt.highlights) {
    for (const source of sourcesFor(rt, code)) {
      map.setFeatureState({ source, id: code }, { [HL_KEY]: kind });
    }
  }
}

/**
 * Frame a geographic box.
 *
 * `fitBounds` rather than a centre and a zoom, because a subdivision is an
 * extent and not a point: Tennessee is 800 km wide and 180 km tall, and any
 * single zoom that holds it in landscape loses it in portrait. MapLibre solves
 * that against the actual container, every time.
 */
function fitBoundsOn(
  rt: Runtime,
  bounds: readonly [number, number, number, number],
  opts?: { padding?: number; durationMs?: number; maxZoom?: number },
): void {
  const map = rt.map;
  if (!map || !rt.loaded) return;
  stopSpin(rt);
  rt.spinSuppressed = true;
  map.fitBounds(
    [
      [bounds[0], bounds[1]],
      [bounds[2], bounds[3]],
    ],
    {
      padding: opts?.padding ?? 48,
      duration: rt.reducedMotion ? 0 : (opts?.durationMs ?? CAMERA.defaultFlyMs),
      maxZoom: opts?.maxZoom ?? CAMERA.maxZoom,
      bearing: 0,
      pitch: 0,
    },
  );
}

function flushRegions(rt: Runtime, collection: RegionCollection): void {
  const map = rt.map;
  if (!map || !rt.loaded) return;
  const source = map.getSource<GeoJSONSource>(SOURCE.regions);
  if (!source) return;
  source.setData(collection);
}

function flushPins(rt: Runtime): void {
  const map = rt.map;
  if (!map || !rt.loaded) return;
  const source = map.getSource<GeoJSONSource>(SOURCE.pins);
  if (!source) return;
  source.setData(pinCollection(rt.pins));
}

function flushMarks(rt: Runtime): void {
  const map = rt.map;
  if (!map || !rt.loaded) return;
  const source = map.getSource<GeoJSONSource>(SOURCE.marks);
  if (!source) return;
  const data: MarkCollection = markCollection(rt.marks.values());
  source.setData(data);
}

function stopPulse(rt: Runtime): void {
  if (rt.pulseRaf !== null) {
    cancelAnimationFrame(rt.pulseRaf);
    rt.pulseRaf = null;
  }
  rt.pulseStartedAt = null;
  const map = rt.map;
  if (map && rt.loaded && map.getLayer(LAYER.markPulse)) {
    map.setPaintProperty(LAYER.markPulse, MARK_PULSE_OPACITY_PROP, 0);
  }
}

/**
 * The expanding ring.
 *
 * One `setPaintProperty` per frame against a source holding at most two points.
 * That is deliberately the cheapest animation available here: a DOM marker
 * would cost a reflow per frame while the globe is also spinning, which is the
 * classic reason a MapLibre globe drops to 40fps on a phone.
 *
 * NEVER runs under `prefers-reduced-motion`. The static ring and the core dot
 * are separate layers precisely so the mark carries its whole message without
 * moving at all.
 */
function startPulse(rt: Runtime): void {
  if (rt.reducedMotion || rt.disposed) return;
  if (rt.marks.size === 0) return;
  if (rt.pulseRaf !== null) return;
  const map = rt.map;
  if (!map || !rt.loaded || !map.getLayer(LAYER.markPulse)) return;

  rt.pulseStartedAt = performance.now();
  const step = (ts: number): void => {
    const m = rt.map;
    if (rt.disposed || !m || !rt.loaded || rt.marks.size === 0 || rt.pulseStartedAt === null) {
      rt.pulseRaf = null;
      return;
    }
    const phase = pulsePhase(ts - rt.pulseStartedAt);
    m.setPaintProperty(LAYER.markPulse, MARK_PULSE_RADIUS_PROP, pulseRadiusPx(phase));
    m.setPaintProperty(LAYER.markPulse, MARK_PULSE_OPACITY_PROP, pulseOpacity(phase));
    rt.pulseRaf = requestAnimationFrame(step);
  };
  rt.pulseRaf = requestAnimationFrame(step);
}

/**
 * Resolve a canvas-space point to a country code, plus the contenders when the
 * tap is a coin flip.
 *
 * Polygons are probed with `queryRenderedFeatures`. Countries that are smaller
 * than a fingertip at the current zoom — the 29 with no 110m geometry at all,
 * plus every country under `SNAP.subResolutionPx` on screen right now — are
 * resolved by projecting their anchors and measuring screen distance, which is
 * exact and independent of how MapLibre hit-tests. `snapResolve` merges the two.
 */
function resolveSnap(rt: Runtime, x: number, y: number): SnapResolution {
  const map = rt.map;
  if (!map || !rt.loaded) return { cca3: null, contenders: [] };

  const canvas = map.getCanvas();
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  const radius = rt.snapRadiusPx;
  const zoom = map.getZoom();
  const pxPerDeg = globePxPerDeg(zoom);

  const centreLL = map.getCenter();
  const centre: [number, number] = [centreLL.lng, centreLL.lat];

  const regionMode = rt.regions !== null;
  const probes: SnapProbes = {
    polygonAt(px, py) {
      if (px < 0 || py < 0 || px > w || py > h) return null;
      const layers = regionMode ? REGION_PICK_LAYERS : POLYGON_PICK_LAYERS;
      const hits = map.queryRenderedFeatures([px, py], { layers });
      for (const hit of hits) {
        const code = codeOf(hit);
        if (code) return code;
      }
      return null;
    },
    pointCandidates() {
      const out: PointCandidate[] = [];
      // `pickTargets()` is the COUNTRY anchor table. In region mode it would
      // let a tap near Rhode Island snap to a microstate on the other side of
      // the Atlantic, so the point path is simply not run: every subdivision
      // has a polygon, which is the case the point path exists to cover.
      if (regionMode) return out;
      for (const target of pickTargets()) {
        if (!isSubResolution(target, zoom, SNAP.subResolutionPx)) continue;
        // map.project() happily returns coordinates for points behind the
        // planet. Without this, a tap on Spain could snap to Nauru.
        if (!isFrontFacing(centre, target.anchor)) continue;
        const p = map.project([target.anchor[0], target.anchor[1]]);
        const distancePx = Math.hypot(p.x - x, p.y - y);
        if (distancePx > radius) continue;
        out.push({
          cca3: target.cca3,
          distancePx,
          radiusPx: target.angularRadiusDeg * pxPerDeg,
          hasGeometry: target.hasGeometry,
        });
      }
      return out;
    },
    isPointTarget(cca3) {
      if (regionMode) return false;
      const target = pickTarget(cca3);
      return target !== undefined && isSubResolution(target, zoom, SNAP.subResolutionPx);
    },
  };

  return snapResolve(x, y, radius, probes, {
    ringStepPx: SNAP.ringStepPx,
    arcStepPx: SNAP.arcStepPx,
    microBiasPx: SNAP.microBiasPx,
    aimTolerancePx: SNAP.aimTolerancePx,
    pointCertaintyPx: SNAP.pointCertaintyPx,
  });
}

/**
 * Zoom in on an ambiguous tap instead of guessing. Returns true when the
 * camera moved, in which case NO pick is emitted — the player taps again into
 * a view where every contender has its own touch target.
 */
function magnifyForContenders(rt: Runtime, contenders: readonly string[]): boolean {
  const map = rt.map;
  if (!map || !rt.disambiguate) return false;
  const plan = magnifyPlan(contenders, map.getZoom(), {
    separationTargetPx: SNAP.separationTargetPx,
    minZoomStep: SNAP.minMagnifyZoomStep,
    maxZoom: CAMERA.maxZoom,
  });
  if (!plan) return false;
  cameraTo(rt, { center: [plan.center[0], plan.center[1]], zoom: plan.zoom }, SNAP.magnifyMs, false);
  return true;
}

function initialRuntime(props: GlobeProps): Runtime {
  return {
    map: null,
    loaded: false,
    eclipseOn: false,
    eclipseRaf: null,
    disposed: false,
    interactive: props.interactive !== false,
    idleSpin: props.idleSpin === true,
    spinSuppressed: false,
    spinRaf: null,
    spinLastTs: 0,
    reducedMotion: false,
    snapRadiusPx: props.snapRadiusPx ?? SNAP.defaultRadiusPx,
    highlights: new Map(),
    pins: new Map(),
    marks: new Map(),
    regions: null,
    regionData: null,
    pulseStartedAt: null,
    pulseRaf: null,
    wave: null,
    swellRaf: null,
    onPick: props.onPick,
    onReady: props.onReady,
    onDisambiguate: props.onDisambiguate,
    disambiguate: props.disambiguate !== false,
  };
}

export const Globe: React.ForwardRefExoticComponent<
  GlobeProps & React.RefAttributes<GlobeHandle>
> = forwardRef<GlobeHandle, GlobeProps>(function Globe(props, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const runtimeRef = useRef<Runtime | null>(null);
  runtimeRef.current ??= initialRuntime(props);
  const rt = runtimeRef.current;

  // Callbacks are read through the runtime so a changing `onPick` identity
  // never re-creates the map.
  useEffect(() => {
    rt.onPick = props.onPick;
    rt.onReady = props.onReady;
    rt.onDisambiguate = props.onDisambiguate;
    rt.disambiguate = props.disambiguate !== false;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    ensureRuntimeStyles(container.ownerDocument);
    rt.disposed = false;

    const motionQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    rt.reducedMotion = motionQuery?.matches ?? false;

    const shortEdge = Math.min(window.innerWidth, window.innerHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const skin = skinById(props.skin ?? DEFAULT_SKIN);
    const map = new MapLibreMap({
      container,
      // The imagery patch is a pure function over `buildStyle()`'s output: it
      // slots a raster source above the ocean and re-tunes the border, highlight
      // and microstate-dot layers so they stay readable over terrain. With the
      // vector skin it returns the base style untouched.
      // Keep the entire sphere on the Marble treatment for now. The optional
      // real-time day/night terminator remains available to the globe package,
      // but the game does not enable it.
      style: applyImageryPatch(buildStyle(), skin, tuningFor(skin), dpr),
      // 48 held 61 MiB of decoded texture on mobile; 24 holds 33 MiB and the
      // per-step cost of the flight is the same. 24 is the knee.
      maxTileCacheSize: 24,
      // Committed build artefacts. Never revalidate them.
      refreshExpiredTiles: false,
      // Invariant 7 is enforced structurally, not by convention: every tile URL
      // is a relative same-origin path, so anything absolute is a bug.
      transformRequest: (url: string) => {
        if (url.includes('://')) {
          throw new Error(`[globe] refusing off-origin request: ${url}`);
        }
        return { url };
      },
      center: [...HOME_VIEW.center],
      zoom: homeZoom(container),
      bearing: HOME_VIEW.bearing,
      pitch: HOME_VIEW.pitch,
      minZoom: CAMERA.minZoom,
      maxZoom: CAMERA.maxZoom,
      maxPitch: CAMERA.maxPitch,
      // Rotation and pitch make pinpointing harder and cost frames. Off.
      dragRotate: false,
      pitchWithRotate: false,
      rollEnabled: false,
      touchPitch: false,
      renderWorldCopies: false,
      attributionControl: false,
      maplibreLogo: false,
      fadeDuration: 0,
      // Phone gate: never render more than 2x, and drop MSAA on small screens.
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      canvasContextAttributes: {
        antialias: shortEdge >= 768,
        preserveDrawingBuffer: false,
      },
      reduceMotion: rt.reducedMotion ? true : undefined,
    });

    rt.map = map;
    map.touchZoomRotate.disableRotation();

    /*
     * The ocean swell's pattern image.
     *
     * Registered off `styledata` rather than `load`, and NOT off
     * `styleimagemissing`: that event is raised by the tile worker for icons
     * and for fill/line patterns, and a `background-pattern` never goes near
     * it — MapLibre resolves a background's pattern straight out of the image
     * manager at draw time and simply paints nothing when it is absent. So the
     * image has to be pushed, at the first moment the style will accept it,
     * which is `styledata` — a frame or two before `load`.
     */
    let removeWaveListener: (() => void) | null = null;
    if (skin.swell > 0) {
      const wave = createWaveImage({ animated: !rt.reducedMotion });
      rt.wave = wave;
      const addWave = (): void => {
        if (rt.disposed || map.hasImage(WAVE_IMAGE_ID)) return;
        map.addImage(WAVE_IMAGE_ID, wave, { pixelRatio: WAVE_PIXEL_RATIO });
      };
      map.on('styledata', addWave);
      removeWaveListener = () => map.off('styledata', addWave);
      if (map.isStyleLoaded()) addWave();
    }

    const onLoad = (): void => {
      if (rt.disposed) return;
      rt.loaded = true;
      // Explicit, even though the style declares it — this survives any future
      // style swap and is the documented v5 API (maplibre-gl 5.24.0).
      map.setProjection({ type: 'globe' });
      applyInteractive(map, rt.interactive);
      flushHighlights(rt);
      flushRegions(rt, rt.regionData ?? emptyRegionCollection());
      flushPins(rt);
      flushMarks(rt);
      startPulse(rt);
      maybeStartSpin(rt);
      maybeStartSwell(rt);
      // Opt-in, read-only camera geometry for scripts/browser-e2e.ts. Silently
      // does nothing unless the page set `__GEOGAME_GLOBE_PROBE__` before this
      // bundle ran — see probe.ts for why that is the whole security story.
      publishCameraProbe({ map: () => rt.map, loaded: () => rt.loaded });
      rt.onReady?.();
    };
    map.on('load', onLoad);

    const onMotionChange = (e: MediaQueryListEvent): void => {
      rt.reducedMotion = e.matches;
      rt.wave?.setAnimated(!e.matches);
      if (e.matches) {
        stopSpin(rt);
        stopPulse(rt);
        stopSwell(rt);
      } else {
        maybeStartSpin(rt);
        startPulse(rt);
        maybeStartSwell(rt);
      }
    };
    motionQuery?.addEventListener('change', onMotionChange);

    // Pointer events, not mouse events: one code path for touch, pen and
    // mouse, and passive listeners so the compositor is never blocked.
    const canvasContainer = map.getCanvasContainer();
    const active = new Set<number>();
    let downId: number | null = null;
    let downX = 0;
    let downY = 0;
    let downAt = 0;
    let dragged = false;
    let emitted = false;

    const emitPick = (clientX: number, clientY: number): void => {
      const canvas = map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const resolution = resolveSnap(rt, x, y);

      // A coin flip is not an answer. Magnify and let the player choose, at
      // the cost of one tap rather than of the round.
      if (resolution.contenders.length > 1 && magnifyForContenders(rt, resolution.contenders)) {
        emitted = true;
        rt.onDisambiguate?.({ screenPx: [x, y], count: resolution.contenders.length });
        return;
      }

      const ll = map.unproject([x, y]);
      const pick: GlobePick = { cca3: resolution.cca3, lngLat: [ll.lng, ll.lat], screenPx: [x, y] };
      emitted = true;
      rt.onPick?.(pick);
    };

    const tapIsLive = (): boolean =>
      !emitted &&
      !dragged &&
      rt.interactive &&
      rt.loaded &&
      performance.now() - downAt <= TAP_MAX_MS;

    const onPointerDown = (e: PointerEvent): void => {
      active.add(e.pointerId);
      stopSpin(rt);
      rt.spinSuppressed = true;
      if (active.size > 1) {
        dragged = true; // pinch or two-finger gesture, never a tap
        return;
      }
      downId = e.pointerId;
      downX = e.clientX;
      downY = e.clientY;
      downAt = performance.now();
      dragged = false;
      emitted = false;
    };

    const onPointerMove = (e: PointerEvent): void => {
      if (downId === null || e.pointerId !== downId) return;
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > TAP_SLOP_PX) dragged = true;
    };

    const finishPointer = (e: PointerEvent): void => {
      active.delete(e.pointerId);
      if (downId === null || e.pointerId !== downId) return;
      downId = null;
      if (active.size > 0) return;
      // A cancelled pointer is never a tap: the OS or the browser took the
      // gesture (palm rejection, an incoming call, an edge swipe), and locking
      // in a pinpoint answer from that would be worse than losing the tap.
      if (e.type !== 'pointerup') return;
      if (!tapIsLive()) return;
      emitPick(e.clientX, e.clientY);
    };

    /*
     * Safety net for the one case the pointer path can miss. Some mobile
     * browsers deliver `pointercancel` instead of `pointerup` when a handler
     * up the tree consumes the gesture; the DOM `click` still fires, and it
     * does not fire at all for a genuinely cancelled gesture. `emitted` makes
     * this idempotent with the pointerup path, so a tap yields exactly one
     * pick either way. `detail === 0` filters synthetic/keyboard clicks.
     */
    const onClick = (e: MouseEvent): void => {
      if (e.detail === 0 || !tapIsLive()) return;
      emitPick(e.clientX, e.clientY);
    };

    canvasContainer.addEventListener('pointerdown', onPointerDown, { passive: true });
    canvasContainer.addEventListener('click', onClick);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerup', finishPointer, { passive: true });
    window.addEventListener('pointercancel', finishPointer, { passive: true });

    return () => {
      rt.disposed = true;
      stopSpin(rt);
      stopPulse(rt);
      stopSwell(rt);
      canvasContainer.removeEventListener('pointerdown', onPointerDown);
      canvasContainer.removeEventListener('click', onClick);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', finishPointer);
      window.removeEventListener('pointercancel', finishPointer);
      motionQuery?.removeEventListener('change', onMotionChange);
      map.off('load', onLoad);
      removeWaveListener?.();
      retractCameraProbe();
      map.remove();
      rt.map = null;
      rt.wave = null;
      rt.loaded = false;
    };
    // Mount-only on purpose: prop changes are handled by the effects below and
    // by the imperative handle. Re-creating the map would restart WebGL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt]);

  useEffect(() => {
    rt.snapRadiusPx = props.snapRadiusPx ?? SNAP.defaultRadiusPx;
  }, [rt, props.snapRadiusPx]);

  /*
   * SUBDIVISION POLYGONS IN, AND OUT AGAIN.
   *
   * Handing back `undefined` has to empty the source rather than merely stop
   * updating it: the same globe instance survives a player leaving a
   * subdivision match for a country one, and fifty state borders left drawn
   * over a round about Chad would be both wrong and — since the pick layers
   * follow `rt.regions` — unanswerable.
   */
  useEffect(() => {
    const collection = props.regions ?? null;
    rt.regions = collection ? indexRegions(collection) : null;
    rt.regionData = collection;
    flushRegions(rt, collection ?? emptyRegionCollection());
  }, [rt, props.regions]);

  useEffect(() => {
    rt.interactive = props.interactive !== false;
    if (rt.map && rt.loaded) applyInteractive(rt.map, rt.interactive);
  }, [rt, props.interactive]);

  useEffect(() => {
    const on = props.idleSpin === true;
    rt.idleSpin = on;
    if (on) {
      // Turning the prop on is an explicit "spin again" — it clears the
      // suppression a previous tap installed.
      rt.spinSuppressed = false;
      maybeStartSpin(rt);
    } else {
      stopSpin(rt);
    }
  }, [rt, props.idleSpin]);

  useImperativeHandle(
    ref,
    (): GlobeHandle => ({
      flyToCountry(cca3, opts) {
        const region = rt.regions?.get(cca3.toUpperCase());
        if (region) {
          fitBoundsOn(rt, region.bounds, {
            ...(opts?.durationMs !== undefined ? { durationMs: opts.durationMs } : {}),
          });
          return;
        }
        const framing = frameForCountry(cca3);
        if (!framing) return;
        cameraTo(
          rt,
          { center: [...framing.center], zoom: opts?.zoom ?? framing.zoom, bearing: 0 },
          opts?.durationMs ?? CAMERA.defaultFlyMs,
          true,
        );
      },

      /** See `fitBoundsOn`. Frames an extent rather than aiming at a point. */
      fitBounds(bounds, opts) {
        fitBoundsOn(rt, bounds, opts);
      },

      flyToLngLat(lngLat, opts) {
        cameraTo(
          rt,
          { center: [lngLat[0], lngLat[1]], zoom: opts?.zoom ?? 3.2, bearing: 0 },
          opts?.durationMs ?? CAMERA.defaultFlyMs,
          true,
        );
      },

      orientToRegion(region, durationMs) {
        /*
         * Explorer's region hint turns the globe and nothing else.
         *
         * The phone case already had this rule, because a region-fitting zoom
         * undid the close viewport-height opening scale. The same thing was
         * happening on every other viewport and nobody had named it: the hint
         * carries a top-level region, so it is a continent, and a camera wide
         * enough to hold a continent is wider than the home camera. Every
         * Explorer round therefore opened with a pull-back the player did not
         * ask for.
         *
         * `homeZoom` rather than `map.getZoom()`, because the caller starts a
         * round with `resetView` immediately before this and that ease is
         * still in flight here. Reading the live zoom would sample the tail of
         * the PREVIOUS round's reveal, which is the closest the camera ever
         * gets. `homeZoom` is the scale the match booted at and the scale
         * `resetView` is on its way to, so the two commands agree.
         */
        const framing = orientFramingFor(region, homeZoom(rt.map?.getContainer() ?? null));
        if (!framing) return;
        cameraTo(
          rt,
          { center: [...framing.center], zoom: framing.zoom, bearing: 0 },
          durationMs ?? CAMERA.defaultOrientMs,
          false,
        );
      },

      highlight(cca3, kind) {
        const code = cca3.toUpperCase();
        rt.highlights.set(code, kind);
        const map = rt.map;
        if (!map || !rt.loaded) return;
        for (const source of sourcesFor(rt, code)) {
          map.setFeatureState({ source, id: code }, { [HL_KEY]: kind });
        }
      },

      clearHighlights() {
        const map = rt.map;
        if (map && rt.loaded) {
          for (const code of rt.highlights.keys()) {
            for (const source of sourcesFor(rt, code)) {
              map.removeFeatureState({ source, id: code }, HL_KEY);
            }
          }
        }
        rt.highlights.clear();
      },

      /**
       * CHANGE 2 / CHANGE 3. Say something about a country, visibly, whether or
       * not it has a polygon.
       *
       * The feature-state highlight goes on as well, because on a big country
       * it is the better half of the effect. The ring is what makes the same
       * command mean anything on Nauru, which has no polygon at all and whose
       * dot is 2.6 pixels across.
       */
      markCountry(cca3, kind) {
        const code = cca3.toUpperCase();
        // In region mode the anchor comes from the collection that was handed
        // in, because `markAnchor` reads the COUNTRY table and has never heard
        // of Ohio.
        const anchor = rt.regions ? (rt.regions.get(code)?.anchor ?? null) : markAnchor(code);
        if (!anchor) return;
        rt.marks.set(code, { id: code, cca3: code, lngLat: anchor, kind });
        rt.highlights.set(code, kind);
        const map = rt.map;
        if (map && rt.loaded) {
          for (const source of sourcesFor(rt, code)) {
            map.setFeatureState({ source, id: code }, { [HL_KEY]: kind });
          }
        }
        flushMarks(rt);
        // Restart the pulse so a mark landing later still gets its shockwave
        // from phase zero rather than joining one already mid-flight.
        stopPulse(rt);
        startPulse(rt);
      },

      unmarkCountry(cca3) {
        const code = cca3.toUpperCase();
        if (!rt.marks.delete(code)) return;
        rt.highlights.delete(code);
        const map = rt.map;
        if (map && rt.loaded) {
          for (const source of sourcesFor(rt, code)) {
            map.removeFeatureState({ source, id: code }, HL_KEY);
          }
        }
        flushMarks(rt);
        if (rt.marks.size === 0) stopPulse(rt);
      },

      clearMarks() {
        if (rt.marks.size === 0) return;
        const map = rt.map;
        for (const code of rt.marks.keys()) {
          rt.highlights.delete(code);
          if (map && rt.loaded) {
            for (const source of sourcesFor(rt, code)) {
              map.removeFeatureState({ source, id: code }, HL_KEY);
            }
          }
        }
        rt.marks.clear();
        flushMarks(rt);
        stopPulse(rt);
      },

      dropPin(id, lngLat, kind) {
        rt.pins.set(id, { id, lngLat: [lngLat[0], lngLat[1]], kind });
        flushPins(rt);
      },

      removePin(id) {
        if (!rt.pins.delete(id)) return;
        flushPins(rt);
      },

      clearPins() {
        if (rt.pins.size === 0) return;
        rt.pins.clear();
        flushPins(rt);
      },

      /**
       * Whether the globe is honouring `prefers-reduced-motion`. The match
       * screen reads it so that its own reveal choreography — which is DOM, not
       * WebGL, and therefore invisible from here — makes the same choice the
       * camera already made, rather than a second guess at the media query.
       */
      prefersReducedMotion() {
        return rt.reducedMotion;
      },

      /*
       * THE ECLIPSE, AND WHY IT IS ANIMATED.
       *
       * A player whose globe simply loses its borders between two frames has
       * no way to tell a game mechanic from a rendering fault, and will report
       * it as one — which is the exact failure this feature has to avoid. So
       * the shadow CLOSES over the planet and the borders FADE out of it, over
       * most of a second, in front of a caption that says what happened.
       *
       * Two paint properties, driven together off one clock. MapLibre's own
       * transitions cannot be used for the pair: they run per property with
       * their own easing, and the borders would finish before the shadow was
       * halfway, which reads as two separate glitches instead of one event.
       *
       * Reduced motion arrives instantly rather than not at all. The state has
       * to be legible either way; only the theatre is optional.
       */
      setEclipse(on, durationMs) {
        const map = rt.map;
        if (!map || !rt.loaded) return;

        /*
         * EVERY border layer, casings included. Naming the three in `LAYER`
         * here was the bug: over imagery -- which is what the game renders --
         * the line a player sees is the near-black casing this list also
         * carries, and fading only the bright line on top of it left the
         * borders as legible as before.
         */
        const lines = BORDER_LINE_LAYERS;

        const paint = (t: number): void => {
          const frame = eclipseFrame(t, on, rt.eclipseOn);
          try {
            map.setPaintProperty(LAYER.eclipse, 'fill-opacity', frame.shadow);
            for (const id of lines) {
              if (map.getLayer(id)) map.setPaintProperty(id, 'line-opacity', frame.ink);
            }
          } catch {
            // A style swapped mid-animation. The next frame re-reads the map.
          }
        };

        if (rt.eclipseRaf !== null) cancelAnimationFrame(rt.eclipseRaf);
        rt.eclipseRaf = null;

        const ms = durationMs ?? ECLIPSE_MS;
        if (ms <= 0 || rt.reducedMotion) {
          rt.eclipseOn = !on;
          paint(1);
          rt.eclipseOn = on;
          return;
        }

        const started = performance.now();
        const step = (): void => {
          const t = Math.min(1, (performance.now() - started) / ms);
          paint(t);
          if (t < 1) {
            rt.eclipseRaf = requestAnimationFrame(step);
            return;
          }
          rt.eclipseRaf = null;
          rt.eclipseOn = on;
        };
        rt.eclipseRaf = requestAnimationFrame(step);
      },
      setInteractive(v) {
        rt.interactive = v;
        if (rt.map && rt.loaded) applyInteractive(rt.map, v);
      },

      resetView(durationMs) {
        const duration = cameraTo(
          rt,
          {
            center: [...HOME_VIEW.center],
            zoom: homeZoom(rt.map?.getContainer() ?? null),
            bearing: HOME_VIEW.bearing,
            pitch: HOME_VIEW.pitch,
          },
          durationMs ?? CAMERA.defaultOrientMs,
          false,
        );
        const map = rt.map;
        const resume = (): void => {
          rt.spinSuppressed = false;
          maybeStartSpin(rt);
        };
        if (!map || duration === 0) resume();
        else map.once('moveend', resume);
      },
    }),
    [rt],
  );

  return (
    <div
      ref={containerRef}
      className={props.className}
      data-geogame-globe=""
      data-geogame-pick-layer={props.regions ? LAYER.regionsFill : LAYER.countriesFill}
    />
  );
});
